import {
  MapPinTypeVariant,
  MapPinVariant,
} from '@user/components/HereMap/MapPin/types'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  fetchEntityDetailAndProducts,
  selectEntityStatus,
  selectFallbackLocation,
  selectFallbackLocationStatus,
  selectLocation,
  selectMapEntityDetails,
  selectMapEntityVariant,
  setSelectedEntityStatus,
  setSelectedServiceLocationId,
  setSelectedServiceLocations,
} from '@user/store/map'
import { useEffect, useMemo, useRef } from 'react'
import { EntityLocationDTO } from '@user/store/map/typings'
import { useSelector } from 'react-redux'
import { formatAddress } from '@user/features/MapView/utils/formatAddress'
import { ENTITY_TYPES } from '@sales-center/common'
import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import { getSalesFunnelStage } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useData'

type Props = {
  location: EntityLocationDTO
}

export function useData(props: Props) {
  const { location } = props
  const selectedEntity = useSelector(selectMapEntityDetails)
  const entityStatus = useSelector(selectEntityStatus)
  const serviceLocation = useSelector(selectLocation)
  const variant = useSelector(selectMapEntityVariant)
  const fallbackLocation = useSelector(selectFallbackLocation)
  const fallbackEntityDetailsStatus = useSelector(selectFallbackLocationStatus)

  const dispatch = useAppDispatch()

  const locationKey = location
    ? `${location.entityId}-${location.entityType}-${location.serviceLocationId}-${location.multitenant}-${location.pinId}`
    : null

  // Track the last locationKey that was successfully fetched so we can skip
  // the backend call when the user re-hovers a pin they've already seen.
  const lastFetchedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!location) return

    const { multitenant, entities, entityType, entityId, serviceLocationId } =
      location
    const isPestPacPin = entityType?.toLowerCase() === MapPinTypeVariant.PestPac
    if (multitenant) {
      if (entities.length > 0) {
        dispatch(setSelectedServiceLocations(entities))
      }
    } else {
      if (isPestPacPin) {
        return
      }

      dispatch(setSelectedServiceLocationId(serviceLocationId!))

      if (lastFetchedKeyRef.current === locationKey) {
        // Data already loaded for this pin — show immediately, no backend call.
        dispatch(setSelectedEntityStatus('success'))
      } else {
        dispatch(setSelectedEntityStatus('loading'))
        dispatch(
          fetchEntityDetailAndProducts([
            entityType.toLowerCase() as MapPinVariant,
            entityId,
            serviceLocationId!,
          ]),
        ).then((result) => {
          // Only mark as fetched after a successful response — if the fetch
          // fails the next hover must retry rather than skip to 'success'.
          if (fetchEntityDetailAndProducts.fulfilled.match(result)) {
            lastFetchedKeyRef.current = locationKey
          }
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, locationKey])

  const nameAndCompanyDisplay = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const companyName = serviceLocation.company
    const name = `${serviceLocation.firstName} ${serviceLocation.lastName}`
    const items = [name, companyName].filter((x) => x && x.trim().length > 0)
    if (items.length > 1) {
      return items.join(' / ')
    } else {
      return items[0] || ''
    }
  }, [serviceLocation])

  const addressDisplay = useMemo(
    () =>
      selectedEntity && selectedEntity.entityType === ENTITY_TYPES.DraftLead
        ? formatAddress(
            selectedEntity?.street1 || '',
            selectedEntity?.street2 || '',
            selectedEntity?.city || '',
            selectedEntity?.stateAbbreviation || '',
            selectedEntity?.postalCode || '',
          )
        : serviceLocation && serviceLocation.contact
          ? formatAddress(
              serviceLocation.contact.street1,
              serviceLocation.contact.street2,
              serviceLocation.contact.city,
              serviceLocation.contact.stateAbbreviation,
              serviceLocation.contact.postalCode,
            )
          : '',

    [selectedEntity, serviceLocation],
  )

  const entityType = useMemo(() => {
    switch (variant?.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
      case 'pendinglead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  const formattedPhone = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const phone = serviceLocation.phoneNumber
    return phone ? formatUSPhoneNumber(phone).trim() || phone : ''
  }, [serviceLocation])

  const status = useMemo(() => {
    return getSalesFunnelStage(entityType, selectedEntity?.salesFunnelStage)
  }, [entityType, selectedEntity])

  return {
    addressDisplay,
    entityType,
    fallbackLocation,
    formattedPhone,
    nameAndCompanyDisplay,
    selectedEntity,
    serviceLocation,
    variant,
    entityStatus,
    fallbackEntityDetailsStatus,
    status,
  }
}

export type UseDataReturn = ReturnType<typeof useData>
